import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import {
  Compass, Trophy, ShieldCheck, Flame, Droplets, Wind, DollarSign, Snowflake,
  Building2, Anchor, ThermometerSun, AlertOctagon,
} from "lucide-react";
import { useI18n } from "@/lib/i18n";

// Keep backend payload values in Turkish (server expects these literal strings)
const ELEMENT_VALUES = ["Dış Duvar", "Çatı", "Döşeme", "İç Duvar"];

export default function SmartAdvisor({ conditions }) {
  const { t, lang } = useI18n();
  const isLight = conditions?._mode === "day";

  // Theme palette
  const heroBg     = isLight ? "#F1F5F9" : "#0F1F22";
  const heroBorder = isLight ? "#1E40AF" : "var(--neon-cyan)";
  const panelBg    = isLight ? "#FFFFFF" : "#1E293B";
  const panelInner = isLight ? "#F8FAFC" : "#0A0A0A";
  const panelBorder = isLight ? "#CBD5E1" : "#1F1F1F";
  const titleColor = isLight ? "#1E293B" : "#FFFFFF";
  const mutedColor = isLight ? "#64748B" : "#8B8B95";
  const labelColor = isLight ? "#475569" : "#8B8B95";
  const accentColor = isLight ? "#1E40AF" : "var(--neon-cyan)";
  const winnerBg   = isLight ? "#ECFDF5" : "#0F2614";
  const winnerBorder = isLight ? "#059669" : "var(--neon-green)";
  const winnerAccent = isLight ? "#059669" : "var(--neon-green)";
  const prosColor  = isLight ? "#047857" : "#C0FFC0";
  const consColor  = isLight ? "#B91C1C" : "#FFC0C0";

  const inputCls = isLight
    ? "rounded-none border-[#CBD5E1] bg-white text-[#1E293B] focus-visible:ring-0 focus-visible:border-[#1E40AF] h-9 font-mono text-sm"
    : "rounded-none border-[#1F1F1F] bg-[#0A0A0A] text-white focus-visible:ring-0 focus-visible:border-[var(--neon-cyan)] h-9 font-mono text-sm";

  const CRITERIA = [
    { key: "thermal",      label: t("advisor.criterion.thermal", "Thermal"),      icon: ThermometerSun, color: "#00E5FF" },
    { key: "fire",         label: t("advisor.criterion.fire", "Fire"),            icon: Flame,          color: "#FF3B30" },
    { key: "condensation", label: t("advisor.criterion.cond", "Condensation"),    icon: Droplets,       color: "#7CFF14" },
    { key: "durability",   label: t("advisor.criterion.dura", "Durability"),      icon: ShieldCheck,    color: "#FFCC00" },
    { key: "cost",         label: t("advisor.criterion.cost", "Cost"),            icon: DollarSign,     color: "#FF8C42" },
  ];

  const elementLabel = (v) => ({
    "Dış Duvar": t("advisor.elem.extwall", "External Wall"),
    "Çatı":      t("advisor.elem.roof", "Roof"),
    "Döşeme":    t("advisor.elem.floor", "Floor"),
    "İç Duvar":  t("advisor.elem.intwall", "Internal Wall"),
  })[v] || v;

  const [cities, setCities] = useState([]);
  const [params, setParams] = useState({
    city: "Trabzon",
    building_element: "Dış Duvar",
    building_height_m: 18,
    fire_zone_strict: false,
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/cities").then(({ data }) => setCities(data)).catch(() => {});
  }, []);

  useEffect(() => {
    let active = true;
    const tmo = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.post("/smart-advisor", { ...params, lang });
        if (active) setData(data);
      } catch { /* */ }
      setLoading(false);
    }, 200);
    return () => { active = false; clearTimeout(tmo); };
  }, [params, lang]);

  const radarData = useMemo(() => {
    if (!data) return [];
    return CRITERIA.map((c) => {
      const point = { criterion: c.label };
      data.ranked.forEach((m) => { point[m.name] = m.scores[c.key]; });
      return point;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const barData = useMemo(() => {
    if (!data) return [];
    return data.ranked.map((m) => ({
      name: m.name.length > 18 ? m.name.slice(0, 17) + "…" : m.name,
      Toplam: m.total_score,
      blocked: m.blocked,
    }));
  }, [data]);

  return (
    <div className="space-y-4" data-testid="smart-advisor">
      {/* Hero */}
      <div className="border-2 p-5"
        style={{ backgroundColor: heroBg, borderColor: heroBorder }}
        data-testid="smart-advisor-hero">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 border flex items-center justify-center flex-shrink-0"
            style={{ borderColor: accentColor }}>
            <Compass className="w-7 h-7" style={{ color: accentColor }}/>
          </div>
          <div className="flex-1">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] font-bold"
              style={{ color: accentColor }}>
              {t("advisor.hero.kicker", "CLIMATE-SMART SELECTION ADVISOR")}
            </div>
            <h2 className="text-2xl font-bold tracking-tight" style={{ color: titleColor }}>
              {t("advisor.hero.headline", "Smart Insulation Advisor")}
            </h2>
            <p className="font-mono text-[11px] leading-relaxed mt-1" style={{ color: mutedColor }}>
              {t("advisor.hero.subtitle", "Scores insulation material classes against 5 criteria — thermal · fire · condensation · durability · cost — based on Turkish city + building element + building height context.")}
            </p>
          </div>
        </div>
      </div>

      {/* Parameters */}
      <div className="border p-4"
        style={{ backgroundColor: panelBg, borderColor: panelBorder }}
        data-testid="advisor-params">
        <h3 className="text-lg font-semibold tracking-tight mb-3"
          style={{ color: '#1E40AF', WebkitTextFillColor: '#1E40AF', fontWeight: 700, opacity: 1 }}>
          {t("advisor.params.title", "Project Context")}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-wider font-bold"
              style={{ color: labelColor }}>
              {t("advisor.params.city", "City")}
            </Label>
            <Select value={params.city} onValueChange={(v) => setParams({ ...params, city: v })}>
              <SelectTrigger className={inputCls} data-testid="advisor-city">
                <SelectValue/>
              </SelectTrigger>
              <SelectContent className="rounded-none max-h-72"
                style={{ backgroundColor: panelInner, borderColor: panelBorder, color: titleColor }}>
                {cities.map((c) => (
                  <SelectItem key={c.city} value={c.city} className="rounded-none font-mono text-xs">
                    {c.city} · {t("advisor.params.zonePrefix", "Zone")} {c.zone}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-wider font-bold"
              style={{ color: labelColor }}>
              {t("advisor.params.element", "Building Element")}
            </Label>
            <Select value={params.building_element}
              onValueChange={(v) => setParams({ ...params, building_element: v })}>
              <SelectTrigger className={inputCls} data-testid="advisor-element">
                <SelectValue>{elementLabel(params.building_element)}</SelectValue>
              </SelectTrigger>
              <SelectContent className="rounded-none"
                style={{ backgroundColor: panelInner, borderColor: panelBorder, color: titleColor }}>
                {ELEMENT_VALUES.map((e) => (
                  <SelectItem key={e} value={e} className="rounded-none font-mono text-xs">
                    {elementLabel(e)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-wider font-bold"
              style={{ color: labelColor }}>
              {t("advisor.params.height", "Building Height (m)")}
            </Label>
            <Input type="number" value={params.building_height_m} className={inputCls}
              onChange={(e) => setParams({ ...params, building_height_m: Number(e.target.value) })}
              data-testid="advisor-height"/>
          </div>
          <div className="flex items-end justify-between gap-3 border p-3"
            style={{ backgroundColor: panelInner, borderColor: panelBorder }}>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-wider font-bold"
                style={{ color: labelColor }}>
                {t("advisor.params.fireZone", "Fire-Risk Zone")}
              </Label>
              <p className="font-mono text-[10px]" style={{ color: mutedColor }}>
                {t("advisor.params.fireHint", "Hospital / school / mall")}
              </p>
            </div>
            <Switch checked={params.fire_zone_strict}
              onCheckedChange={(v) => setParams({ ...params, fire_zone_strict: v })}
              data-testid="advisor-fire-switch"/>
          </div>
        </div>
      </div>

      {!data && loading && (
        <div className="border p-8 text-center font-mono text-sm"
          style={{ backgroundColor: panelBg, borderColor: panelBorder, color: accentColor }}>
          {t("advisor.loading", "Analyzing context...")}
        </div>
      )}

      {data && (
        <>
          {/* Context summary */}
          <div className="border p-4"
            style={{ backgroundColor: panelBg, borderColor: panelBorder }}
            data-testid="context-summary">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold tracking-tight"
                style={{ color: '#00CED1', WebkitTextFillColor: '#00CED1', fontWeight: 700, opacity: 1 }}>
                {t("advisor.context.title", "Geographic Context")}
              </h3>
              <span className="font-mono text-[10px] uppercase tracking-wider"
                style={{ color: accentColor }}>
                {t("advisor.context.zoneTag", "TS 825:2024 ZONE")} {data.zone}
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px border"
              style={{ borderColor: panelBorder, backgroundColor: panelBorder }}>
              <div className="p-3" style={{ backgroundColor: panelInner }}>
                <div className="font-mono text-[10px] uppercase tracking-wider font-bold flex items-center gap-1"
                  style={{ color: labelColor }}>
                  <Flame className="w-3 h-3 text-[#FF3B30]"/>HDD
                </div>
                <div className="font-mono text-lg font-semibold" style={{ color: titleColor }}>{data.hdd}</div>
              </div>
              <div className="p-3" style={{ backgroundColor: panelInner }}>
                <div className="font-mono text-[10px] uppercase tracking-wider font-bold flex items-center gap-1"
                  style={{ color: labelColor }}>
                  <Snowflake className="w-3 h-3" style={{ color: accentColor }}/>CDD
                </div>
                <div className="font-mono text-lg font-semibold" style={{ color: titleColor }}>{data.cdd}</div>
              </div>
              <div className="p-3" style={{ backgroundColor: panelInner }}>
                <div className="font-mono text-[10px] uppercase tracking-wider font-bold"
                  style={{ color: labelColor }}>
                  {t("advisor.context.utarget", "U-TARGET")}
                </div>
                <div className="font-mono text-lg font-semibold" style={{ color: accentColor }}>{data.u_target}</div>
                <div className="font-mono text-[10px]" style={{ color: mutedColor }}>W/m²K</div>
              </div>
              <div className="p-3" style={{ backgroundColor: panelInner }}>
                <div className="font-mono text-[10px] uppercase tracking-wider font-bold"
                  style={{ color: labelColor }}>
                  {t("advisor.context.tags", "STATUS TAGS")}
                </div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {data.is_high_rise && <span className="font-mono text-[10px] px-1.5 py-0.5 border" style={{ backgroundColor: "#2A0F0F", color: "#FF3B30", borderColor: "#FF3B30" }}><Building2 className="inline w-3 h-3 mr-0.5"/>{t("advisor.tag.highrise", "HIGH-RISE")}</span>}
                  {data.is_coastal   && <span className="font-mono text-[10px] px-1.5 py-0.5 border" style={{ backgroundColor: "#0A1A1F", color: accentColor, borderColor: accentColor }}><Anchor className="inline w-3 h-3 mr-0.5"/>{t("advisor.tag.coastal", "COASTAL")}</span>}
                  {data.is_cold      && <span className="font-mono text-[10px] px-1.5 py-0.5 border" style={{ backgroundColor: "#0F1A2A", color: "#7CB8FF", borderColor: "#7CB8FF" }}><Snowflake className="inline w-3 h-3 mr-0.5"/>{t("advisor.tag.cold", "COLD")}</span>}
                  {data.fire_strict  && <span className="font-mono text-[10px] px-1.5 py-0.5 border" style={{ backgroundColor: "#2A0F0F", color: "#FF3B30", borderColor: "#FF3B30" }}><Flame className="inline w-3 h-3 mr-0.5"/>{t("advisor.tag.firestrict", "FIRE-STRICT")}</span>}
                </div>
              </div>
            </div>
            <div className="mt-3 space-y-1 font-mono text-[11px]" style={{ color: mutedColor }}>
              {data.context_notes.map((n, i) => (
                <p key={i} className="leading-relaxed">→ {n}</p>
              ))}
            </div>
          </div>

          {/* WINNER */}
          {data.winner && (
            <div className="border-2 p-5"
              style={{ backgroundColor: winnerBg, borderColor: winnerBorder }}
              data-testid="winner-card">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 border-2 flex items-center justify-center flex-shrink-0"
                  style={{ borderColor: winnerBorder }}>
                  <Trophy className="w-8 h-8" style={{ color: winnerAccent }}/>
                </div>
                <div className="flex-1">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em]"
                    style={{ color: winnerAccent }}>
                    {t("advisor.winner.kicker", "PROFESSOR'S PICK · RECOMMENDED CHOICE")}
                  </div>
                  <h3 className="text-2xl font-bold tracking-tight mt-1" style={{ color: titleColor }}>{data.winner.name}</h3>
                  <div className="font-mono text-[11px] mt-1" style={{ color: mutedColor }}>
                    {t("advisor.winner.brand", "Brand example:")} {data.winner.brand_example} · λ={data.winner.lambda_value} · {t("advisor.criterion.fire", "Fire")}: {data.winner.fire_class}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-[10px] uppercase tracking-wider" style={{ color: mutedColor }}>
                    {t("advisor.winner.totalScore", "TOTAL SCORE")}
                  </div>
                  <div className="font-mono text-4xl font-bold" style={{ color: winnerAccent }}>{data.winner.total_score}</div>
                  <div className="font-mono text-[10px]" style={{ color: mutedColor }}>/ 100</div>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="border p-3" style={{ backgroundColor: panelInner, borderColor: panelBorder }}>
                  <div className="font-mono text-[10px] uppercase tracking-wider font-bold flex items-center gap-1"
                    style={{ color: labelColor }}>
                    <Wind className="w-3 h-3" style={{ color: accentColor }}/> {t("advisor.winner.thickness", "RECOMMENDED THICKNESS")}
                  </div>
                  <div className="font-mono text-xl font-semibold mt-1" style={{ color: accentColor }}>
                    {data.winner.recommended_thickness_cm} cm
                  </div>
                  <div className="font-mono text-[10px]" style={{ color: mutedColor }}>
                    {t("advisor.winner.thicknessFor", "to meet U={u} W/m²K target").replace("{u}", data.u_target)}
                  </div>
                </div>
                <div className="border p-3" style={{ backgroundColor: panelInner, borderColor: panelBorder }}>
                  <div className="font-mono text-[10px] uppercase tracking-wider font-bold" style={{ color: labelColor }}>
                    {t("advisor.winner.pros", "PROS")}
                  </div>
                  <ul className="font-mono text-[11px] space-y-0.5 mt-1">
                    {data.winner.pros.map((p, i) => (
                      <li key={i} style={{ color: prosColor }}>+ {p}</li>
                    ))}
                  </ul>
                </div>
                <div className="border p-3" style={{ backgroundColor: panelInner, borderColor: panelBorder }}>
                  <div className="font-mono text-[10px] uppercase tracking-wider font-bold" style={{ color: labelColor }}>
                    {t("advisor.winner.cons", "CAUTION")}
                  </div>
                  <ul className="font-mono text-[11px] space-y-0.5 mt-1">
                    {data.winner.cons.map((p, i) => (
                      <li key={i} style={{ color: consColor }}>! {p}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Comparison dashboard */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="border p-4"
              style={{ backgroundColor: panelBg, borderColor: panelBorder }}
              data-testid="radar-card">
              <h3 className="text-base font-semibold tracking-tight mb-2" style={{ color: titleColor }}>
                {t("advisor.radar.title", "Multi-Criteria Comparison · Radar")}
              </h3>
              <div style={{ height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData}>
                    <PolarGrid stroke={isLight ? "#CBD5E1" : "#1F1F1F"}/>
                    <PolarAngleAxis dataKey="criterion"
                      tick={{ fontSize: 10, fontFamily: "IBM Plex Mono", fill: isLight ? "#475569" : "#8B8B95" }}/>
                    <PolarRadiusAxis angle={90} domain={[0, 100]}
                      tick={{ fontSize: 9, fontFamily: "IBM Plex Mono", fill: isLight ? "#94A3B8" : "#52525B" }}/>
                    {data.ranked.slice(0, 3).map((m, i) => {
                      const colors = ["#39FF14", "#00E5FF", "#FFCC00"];
                      return (
                        <Radar key={m.id} name={m.name} dataKey={m.name}
                          stroke={colors[i]} fill={colors[i]} fillOpacity={0.18}/>
                      );
                    })}
                    <Tooltip contentStyle={{ background: isLight ? "#FFFFFF" : "#0A0A0A",
                      border: `1px solid ${isLight ? "#1E40AF" : "#00E5FF"}`,
                      fontFamily: "IBM Plex Mono", fontSize: 11, color: isLight ? "#1E293B" : "#FFF" }}/>
                    <Legend wrapperStyle={{ fontFamily: "IBM Plex Mono", fontSize: 9, color: titleColor }}/>
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="border p-4"
              style={{ backgroundColor: panelBg, borderColor: panelBorder }}
              data-testid="bar-card">
              <h3 className="text-base font-semibold tracking-tight mb-2" style={{ color: titleColor }}>
                {t("advisor.bar.title", "Total Score Ranking")}
              </h3>
              <div style={{ height: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barData} layout="vertical" margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                    <CartesianGrid stroke={isLight ? "#CBD5E1" : "#1F1F1F"}/>
                    <XAxis type="number" domain={[0, 100]}
                      tick={{ fontSize: 10, fontFamily: "IBM Plex Mono", fill: isLight ? "#475569" : "#8B8B95" }}/>
                    <YAxis type="category" dataKey="name" width={120}
                      tick={{ fontSize: 10, fontFamily: "IBM Plex Mono", fill: isLight ? "#1E293B" : "#FFFFFF" }}/>
                    <Tooltip contentStyle={{ background: isLight ? "#FFFFFF" : "#0A0A0A",
                      border: `1px solid ${isLight ? "#1E40AF" : "#00E5FF"}`,
                      fontFamily: "IBM Plex Mono", fontSize: 11, color: isLight ? "#1E293B" : "#FFF" }}/>
                    <Bar dataKey="Toplam" isAnimationActive={false}>
                      {barData.map((entry, i) => (
                        <Cell key={i} fill={entry.blocked ? "#FF3B30" : i === 0 ? "#39FF14" : i === 1 ? "#00E5FF" : "#FFCC00"}/>
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Ranked detailed table */}
          <div className="border"
            style={{ backgroundColor: panelBg, borderColor: panelBorder }}
            data-testid="ranking-table">
            <div className="px-4 py-2 border-b flex items-center justify-between"
              style={{ backgroundColor: panelInner, borderColor: panelBorder }}>
              <span className="font-mono text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: titleColor }}>
                {t("advisor.table.title", "Detailed Ranking")}
              </span>
              <span className="font-mono text-[10px]" style={{ color: mutedColor }}>
                {t("advisor.table.weights", "Weights:")} T {Math.round(data.weights.thermal*100)}% · F {Math.round(data.weights.fire*100)}% ·
                C {Math.round(data.weights.condensation*100)}% · D {Math.round(data.weights.durability*100)}% ·
                $ {Math.round(data.weights.cost*100)}%
              </span>
            </div>
            <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b font-mono text-[10px] uppercase tracking-wider font-bold"
              style={{ backgroundColor: panelInner, borderColor: panelBorder, color: labelColor }}>
              <div className="col-span-1">#</div>
              <div className="col-span-3">{t("advisor.table.material", "MATERIAL")}</div>
              <div className="col-span-1 text-center">T</div>
              <div className="col-span-1 text-center">F</div>
              <div className="col-span-1 text-center">C</div>
              <div className="col-span-1 text-center">D</div>
              <div className="col-span-1 text-center">$</div>
              <div className="col-span-1 text-center">{t("advisor.table.total", "TOTAL")}</div>
              <div className="col-span-2 text-right">{t("advisor.table.rec", "RECOMMENDATION")}</div>
            </div>
            {data.ranked.map((m, i) => (
              <div key={m.id}
                className="grid grid-cols-12 gap-2 items-center px-4 py-2.5 border-b"
                style={{
                  borderColor: panelBorder,
                  backgroundColor: m.blocked
                    ? (isLight ? "#FEF2F2" : "rgba(42,15,15,0.4)")
                    : i === 0
                      ? (isLight ? "#ECFDF5" : "rgba(10,38,20,0.4)")
                      : "transparent",
                }}
                data-testid={`ranking-row-${m.id}`}>
                <div className="col-span-1 font-mono text-sm font-semibold"
                  style={{ color: i === 0 ? winnerAccent : mutedColor }}>
                  {m.blocked ? <AlertOctagon className="w-4 h-4 text-[#FF3B30]"/> : `#${i + 1}`}
                </div>
                <div className="col-span-3">
                  <div className="text-sm font-medium" style={{ color: titleColor }}>{m.name}</div>
                  <div className="font-mono text-[10px]" style={{ color: mutedColor }}>{m.fire_class} · λ={m.lambda_value}</div>
                </div>
                {CRITERIA.map((c) => (
                  <div key={c.key} className="col-span-1 text-center">
                    <div className="font-mono text-sm" style={{ color: c.color }}>
                      {m.scores[c.key]}
                    </div>
                  </div>
                ))}
                <div className="col-span-1 text-center">
                  <div className="font-mono text-base font-semibold"
                    style={{ color: m.blocked ? "#FF3B30" : i === 0 ? winnerAccent : titleColor }}>
                    {m.total_score}
                  </div>
                </div>
                <div className="col-span-2 text-right font-mono text-xs">
                  {m.blocked ? (
                    <span style={{ color: "#FF3B30" }}>{t("advisor.table.blocked", "BLOCKED · Fire")}</span>
                  ) : (
                    <span style={{ color: mutedColor }}>{m.recommended_thickness_cm} cm</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
